import { ChevronDown, ChevronRight } from 'lucide-react'
import { useContext, useState } from 'react'

import BrokersMemberContext from 'components/context/BrokersMemberContext'
import CreateEnrollmentButton from 'components/CreateEnrollmentButton'
import AnimateHeightAndOpacity from 'components/reusable/AnimateHeightAndOpacity'
import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { Banner, Button } from 'slab'
import { formatMoney } from 'utils'
import { useGetBrokersMembersMemberIdEnrollmentsCostOfCoverage } from 'utils/web_api'
import { type BrokersMoney } from 'utils/web_api.schemas'

import MemberCostOfCoverage from './MemberCostOfCoverage'

interface BrokersMembersEnrollmentsProps {
  memberId: string
  isTieredAllowancesEmployer: boolean
  isUserThatchAdmin: boolean
}

const BrokersMembersEnrollmentsSummary = ({
  memberId,
  isTieredAllowancesEmployer,
  isUserThatchAdmin,
}: BrokersMembersEnrollmentsProps) => {
  const { planYear } = useContext(BrokersMemberContext)
  const [showDetails, setShowDetails] = useState(false)

  const now = new Date()
  const currentMonth = now.getMonth() + 1 // getMonth gives 0-based index - add 1 to shift to 1-based
  const currentYear = now.getFullYear()

  let summaryCostParams = null
  if (memberId && planYear && currentYear <= planYear) {
    summaryCostParams = {
      year: planYear,
      // For current year, use current month
      // For future years, use Jan
      month: currentYear === planYear ? currentMonth : 1
    }
  }

  const {
    data: summaryCostData,
    isLoading,
    error,
  } = useGetBrokersMembersMemberIdEnrollmentsCostOfCoverage(memberId, {
    plan_year: summaryCostParams?.year || 0,
    start_month: summaryCostParams?.month || 1,
    end_month: summaryCostParams?.month || 1,
  }, {
    query: {
      enabled: summaryCostParams !== null,
      refetchOnWindowFocus: false,
    },
  })

  const handleViewDetails = () => {
    setShowDetails(!showDetails)
  }

  if (isLoading) {
    return (
      <div className='flex justify-between items-center mb-2'>
        <SkeletonItem widthClass='w-80' heightClass='h-10' />
        {planYear && (!isTieredAllowancesEmployer || isUserThatchAdmin) && (
          <div className='flex w-40'>
            <CreateEnrollmentButton memberId={memberId} planYear={planYear} isTieredAllowancesEmployer={isTieredAllowancesEmployer} />
          </div>
        )}
      </div>
    )
  }

  if (error) {
    return (
      <div className='flex justify-between items-center mb-2'>
        <Banner type='critical' title='Error loading cost of coverage' />
        {(!isTieredAllowancesEmployer || isUserThatchAdmin) && planYear && (
          <div className='flex w-40'>
            <CreateEnrollmentButton memberId={memberId} planYear={planYear} isTieredAllowancesEmployer={isTieredAllowancesEmployer} />
          </div>
        )}
      </div>
    )
  }

  const summaryCost = summaryCostData && summaryCostParams
    ? summaryCostData.monthly_costs[summaryCostParams.month.toString()]
    : null
  const formattedSummaryCost = summaryCost
    ? formatMoney(parseFloat((summaryCost as BrokersMoney).value || '0'))
    : null
  const formattedSummaryCostDate = summaryCostParams
    ? new Intl.DateTimeFormat('en-US', {
      month: 'short', // e.g. "Jan"
      year: 'numeric', // e.g. 2026
    }).format(new Date(summaryCostParams.year, summaryCostParams.month - 1)) // Month passed as 0-based index val
    : null

  return (
    <div className='mb-4'>
      {/* Current month display with create enrollment button */}
      <div className='flex justify-between items-center'>
        <div className='flex items-center justify-between gap-2'>
          <div className='flex items-center heading-md text-gray-600'>
            Plan enrollments
            <span className='ml-2 text-sm'>
              ({formattedSummaryCost} total cost for {formattedSummaryCostDate})
            </span>
          </div>

          <Button
            onClick={handleViewDetails}
            variant='tertiary'
            rightIcon={showDetails ? ChevronDown : ChevronRight}
          >
            {showDetails ? 'Hide monthly total costs' : 'View monthly total costs'}
          </Button>
        </div>
        {/*
         * We allow thatch admins (and thatch admins only) to create enrollments for tiered allowances employers.
         * When this is done, it's required that the max_premium_reimbursement_amount is set, and
         * we entrust our ops team to set the correct value.  External brokers have not set this appropriately in
         * the past.
         */}
        {(!isTieredAllowancesEmployer || isUserThatchAdmin) && planYear && (
          <div className='flex w-40'>
            <CreateEnrollmentButton memberId={memberId} planYear={planYear} isTieredAllowancesEmployer={isTieredAllowancesEmployer} />
          </div>
        )}
      </div>

      <AnimateHeightAndOpacity isOpen={showDetails}>
        <div className='py-4'>
          <MemberCostOfCoverage
            memberId={memberId}
            isDisplayed={showDetails}
          />
        </div>
      </AnimateHeightAndOpacity>
    </div>
  )
}

export default BrokersMembersEnrollmentsSummary
